import { type GanttBar } from '@apm/contracts';
import { isCompletedLate } from '../../lib/overdue';
import { dayDiff, parseDate, startOfDay } from './scale';

/**
 * Tình trạng lịch của một thanh Gantt — APM-GANTT-l.
 *
 * ## Vì sao màu thanh nói về **lịch**, không phải về đường găng
 *
 * Trước đây màu thanh dồn bốn nghĩa vào hai màu: vàng vừa là "đường găng" vừa
 * là "mất người", đỏ vừa là "vi phạm phụ thuộc" vừa là "mất người mức nặng".
 * Nhìn một biểu đồ vàng rực thì không đoán được đó là dự án đang trễ hay chỉ là
 * một chuỗi việc nối đuôi nhau — mà hai chuyện ấy đòi hai phản ứng khác nhau.
 *
 * Nay mỗi kênh thị giác nói **một** chuyện:
 *
 * | Kênh                       | Nói về                                  |
 * | -------------------------- | --------------------------------------- |
 * | Màu thân thanh             | tình trạng lịch (hàm này)               |
 * | Viền nét đứt               | mất người (APM-1312)                    |
 * | ▲ ở danh sách + mũi tên đỏ | đường găng                              |
 *
 * Đường găng **không** mất chỗ hiển thị: nó vẫn có dấu ▲ trước tên việc, mũi
 * tên phụ thuộc vẫn đỏ, và bộ lọc "chỉ đường găng" vẫn còn. Nó chỉ thôi tranh
 * chỗ với một thông tin mà người xem cần thường xuyên hơn.
 *
 * Bốn mức dưới đây đúng theo thang RAG mà `--color-rag-*` đã đặt tên sẵn — cùng
 * thang mà báo cáo trạng thái dự án vẫn dùng, nên không phải học thêm bảng màu
 * thứ hai.
 */
export type ScheduleHealth =
  /** Đã đóng. Xanh — và nó **thắng** mọi cảnh báo về lịch: việc xong rồi thì
   *  không còn gì để làm với nó, tô vàng chỉ mời người dùng đi tìm một việc
   *  không tồn tại. */
  | 'done'
  /** Đã đóng, nhưng đóng **sau** hạn — người dùng yêu cầu 2026-09-30.
   *
   *  Không phải một cảnh báo (việc xong rồi, không còn gì để làm), nên nó mờ
   *  như `done`; nhưng là dữ kiện về lịch mà người xem Gantt cần thấy — "việc
   *  nào trễ" không được biến mất chỉ vì việc ấy đã đóng. Không biết ngày xong
   *  (`actualEndAt` trống: dữ liệu cũ, Replay) thì là `done`, không bịa. */
  | 'doneLate'
  /** Quá hạn: hết ngày kết thúc mà chưa đóng. */
  | 'overdue'
  /** Chưa quá hạn nhưng `%` hoàn thành thấp hơn phần thời gian đã trôi qua. */
  | 'behind'
  /** Đúng tiến độ, hoặc chưa tới lượt. */
  | 'onTrack';

/**
 * Dung sai, tính bằng **điểm phần trăm**.
 *
 * Không có nó thì ngày đầu tiên của mọi việc đều "chậm": thời gian đã trôi một
 * phần, còn `%` thì vẫn 0 vì chưa ai kịp báo gì. Một biểu đồ mà việc nào cũng
 * vàng ngay hôm bắt đầu thì màu vàng thôi mang nghĩa.
 *
 * 10 điểm ≈ một ngày trong một việc mười ngày — đúng khoảng trễ mà không ai gọi
 * là vấn đề.
 */
export const HEALTH_TOLERANCE_PCT = 10;

/**
 * Phần trăm **đáng lẽ** đã xong tại thời điểm `today`, theo lịch tuyến tính.
 *
 * ## Vì sao ngày lịch chứ không phải ngày làm việc
 *
 * Thanh trên biểu đồ được vẽ theo ngày lịch (cuối tuần chỉ tô nền nhạt chứ vẫn
 * chiếm chỗ). Nếu màu của thanh lại tính theo ngày làm việc thì màu sẽ mâu
 * thuẫn với chính chiều dài mà người dùng đang nhìn — một việc trông đã đi được
 * nửa đường mà vẫn xanh, không giải thích được bằng thứ gì trên màn hình.
 *
 * ## Vì sao tuyến tính
 *
 * Đây là ước lượng để **tô màu**, không phải để tính EVM. Một đường cong S sát
 * thực tế hơn nhưng đòi biết hình dạng khối lượng công việc — thứ không có
 * trong dữ liệu — và nó chỉ dịch ngưỡng vàng đi vài ngày.
 *
 * @returns `null` khi không đủ ngày để nói được gì.
 */
export function expectedPct(
  startDate: string | null,
  dueDate: string | null,
  today: Date,
): number | null {
  if (startDate === null || dueDate === null) return null;

  const start = startOfDay(parseDate(startDate));
  const due = startOfDay(parseDate(dueDate));
  const now = startOfDay(today);
  if (due < start) return null;

  if (now < start) return 0;
  if (now > due) return 100;

  /*
    Cả hai đầu tính **trọn ngày**: một việc một ngày (start === due) thì tổng là
    1 ngày, và ngay trong ngày ấy đã kỳ vọng 100%. Chia cho `due - start` trần
    sẽ là chia cho 0.
  */
  const total = dayDiff(new Date(start), new Date(due)) + 1;
  const elapsed = dayDiff(new Date(start), new Date(now)) + 1;
  return Math.round((elapsed / total) * 100);
}

/** Xếp một thanh vào một trong bốn mức. `today` đến từ `asOf` (luật #7). */
export function scheduleHealth(bar: GanttBar, today: Date): ScheduleHealth {
  if (bar.task.statusCategory === 'done') {
    return isCompletedLate(bar.dueDate, 'done', bar.task.actualEndAt) ? 'doneLate' : 'done';
  }

  const due = bar.dueDate;
  if (due !== null && startOfDay(today) > startOfDay(parseDate(due))) return 'overdue';

  const expected = expectedPct(bar.startDate, due, today);
  if (expected === null) return 'onTrack';

  return bar.task.progressPct + HEALTH_TOLERANCE_PCT < expected ? 'behind' : 'onTrack';
}
